import React from 'react';

import PropTypes from 'prop-types';

import { PRODUCT_IDS, TOOL_IDS } from '@adsk/alloy-provider';

import { ConnectedSideNavigation } from './ConnectedSideNavigation';
import { ConnectedTopNavigation } from './ConnectedTopNavigation';
import { PAGE_VARIANTS } from './consts';
import { EntitlementsErrorConditionalRenderer } from './EntitlementsErrorConditionalRenderer';
import MaintenancePageConditionalRenderer from './MaintenancePageConditionalRenderer';
import NavigationLayout from './NavigationLayout';

/**
 * ProjectPageLayoutView is an internal component, used mainly in stories when mocked data or edge cases are needed.
 * Consumers should use `ProjectPageLayout` directly where needed.
 */
const ProjectPageLayoutView = React.forwardRef((props, ref) => {
  const {
    children,
    variant,
    currentToolId,
    showAccountsInHeader,
    renderSideNavigation,
    renderTopNavigation,
    onLogout,
    helpLinks,
    isProductRequired,
    onProductClick,
    onProjectClick,
    onAccountClick,
    renderAboveLayout,
    UNSAFE_showBuildersUserProfileModal,
    UNSAFE_isBuildersProfileModalEnabled,
  } = props;

  return (
    <MaintenancePageConditionalRenderer>
      <EntitlementsErrorConditionalRenderer>
        <NavigationLayout
          ref={ref}
          renderSideNavigation={() =>
            renderSideNavigation({ selectedId: currentToolId })
          }
          renderTopNavigation={() =>
            renderTopNavigation({
              showAccountsInHeader,
              onLogout,
              helpLinks,
              isProductRequired,
              isAccountView: variant === PAGE_VARIANTS.ACCOUNT,
              onProductClick,
              onProjectClick,
              onAccountClick,
              UNSAFE_showBuildersUserProfileModal,
              UNSAFE_isBuildersProfileModalEnabled,
            })
          }
          renderAboveLayout={renderAboveLayout}
        >
          {children}
        </NavigationLayout>
      </EntitlementsErrorConditionalRenderer>
    </MaintenancePageConditionalRenderer>
  );
});

ProjectPageLayoutView.PRODUCT_IDS = PRODUCT_IDS;
ProjectPageLayoutView.TOOL_IDS = TOOL_IDS;
ProjectPageLayoutView.VARIANTS = PAGE_VARIANTS;

ProjectPageLayoutView.displayName = 'ProjectPageLayoutView';

ProjectPageLayoutView.defaultProps = {
  renderSideNavigation: (props) => <ConnectedSideNavigation {...props} />,
  renderTopNavigation: (props) => <ConnectedTopNavigation {...props} />,
};

ProjectPageLayoutView.propTypes = {
  /** Project feature content */
  children: PropTypes.node,
  /**
   * Page variant. Account/Project etc.
   * Needed in cases where the page is in Account mode, or when page need to support both account & project mode.
   */
  variant: PropTypes.oneOf(Object.values(PAGE_VARIANTS)),
  /** The current service ID, to mark as selected in side nav. */
  showAccountsInHeader: PropTypes.bool,
  /** The current tool ID, to mark as selected in side nav. */
  currentToolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  /** Side navigation render function */
  renderSideNavigation: PropTypes.func,
  /** Top navigation render function */
  renderTopNavigation: PropTypes.func,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /** Override default help links */
  helpLinks: ConnectedTopNavigation.propTypes.helpLinks,
  /** is product required */
  isProductRequired: PropTypes.bool,
  /** Custom product click handler */
  onProductClick: PropTypes.func,
  /** Custom project click handler */
  onProjectClick: PropTypes.func,
  /** Custom account click handler */
  onAccountClick: PropTypes.func,
  /** Items that will render above the layout*/
  renderAboveLayout: PropTypes.func,
  /** Triggers opening the Builders Network Profile Modal */
  UNSAFE_showBuildersUserProfileModal: PropTypes.func,
  /** When BuildersNetworkProfile is enabled, it updates the TopNav Profile dropdown options to include button for opening the BN Modal and hides the Profile settings button*/
  UNSAFE_isBuildersProfileModalEnabled: PropTypes.bool,
};

export default ProjectPageLayoutView;
